import { defineStore } from 'pinia'
import { ref } from 'vue'

import { SfLog } from '@app/adapters/logger'
import type { Meeting } from '@app/application/client_meetings/types'
import * as MeetingsApi from '@app/application/client_meetings/api/meetings'
import * as PastApi from '@app/application/client_meetings/api/past'
import { useProposalDetailsStore } from '@app/application/client_meetings/screens/past/stores/proposal'
import { marked } from 'marked'

export const usePastMeetingsStore = defineStore('pastMeetings', () => {
  const orderedMeetingIds = ref<number[]>([])
  const meetingsById = ref<Record<number, Meeting>>({})
  const htmlSummary = ref<Record<number, string>>({})
  const proposalDetailsStore = useProposalDetailsStore()

  function deleteMeeting(meetingId: number): void {
    orderedMeetingIds.value = orderedMeetingIds.value.filter(
      (id: number) => id !== meetingId
    )
    delete meetingsById.value[meetingId]
  }

  async function fetchPastMeetings(): Promise<void> {
    try {
      const pastMeetings = (await PastApi.fetchMeetings()) || []
      await setPastMeetings(pastMeetings)
    } catch (error) {
      SfLog.error('Failed to fetch past meetings', { error })
    }
  }

  async function setPastMeetings(
    pastMeetings: PastApi.Meeting[]
  ): Promise<void> {
    await Promise.all(
      pastMeetings.map(async (meeting) => {
        if (meeting.followUpProposal) {
          await proposalDetailsStore.addProposalDetails(
            meeting.followUpProposal
          )
        }
      })
    )

    orderedMeetingIds.value = pastMeetings.map((meeting) => meeting.id)

    meetingsById.value = pastMeetings.reduce(
      (acc, meeting) => ({
        ...acc,
        [meeting.id]: toStoreMeeting(meeting),
      }),
      {}
    )

    const summaryResults = await Promise.all(
      pastMeetings.map(async (meeting) => ({
        id: meeting.id,
        html: await parseSummary(meeting),
      }))
    )
    htmlSummary.value = summaryResults.reduce(
      (acc, { id, html }) => ({
        ...acc,
        [id]: html,
      }),
      {}
    )
  }

  async function fetchPastMeeting(meetingId: number): Promise<void> {
    try {
      const meeting = await PastApi.fetchMeeting(meetingId)
      if (!meeting) {
        return
      }

      await setPastMeeting(meeting)
    } catch (error) {
      SfLog.error('Failed to fetch past meeting', { error })
    }
  }

  async function setPastMeeting(meeting: PastApi.Meeting): Promise<void> {
    if (meeting.followUpProposal) {
      await proposalDetailsStore.addProposalDetails(meeting.followUpProposal)
    }

    meetingsById.value[meeting.id] = toStoreMeeting(meeting)
    htmlSummary.value[meeting.id] = await parseSummary(meeting)
  }

  function toStoreMeeting(meeting: PastApi.Meeting): Meeting {
    return {
      id: meeting.id,
      title: meeting.title,
      summary: meeting.summary,
      summaryGenerating: meeting.summaryGenerating,
      completedAt: meeting.completedAt,
      order: meeting.order,
      links: meeting.links,
      proposalId: meeting.followUpProposal ? meeting.followUpProposal.id : null,
      discussedTodoIds: meeting.discussedTodoIds,
      attachments: meeting.attachments,
    }
  }

  async function parseSummary(
    meeting: Pick<Meeting, 'summary'>
  ): Promise<string> {
    try {
      return meeting.summary ? await marked.parse(meeting.summary) : ''
    } catch (error) {
      SfLog.error('Error parsing meeting markdown:', { error })
      return ''
    }
  }

  function setMeetingOrder(meetingId: number, newOrder: string[]): void {
    if (meetingsById.value[meetingId]) {
      meetingsById.value[meetingId].order = newOrder
    }
  }

  async function save(meeting: Meeting): Promise<void> {
    try {
      // A user-driven save never writes null; nil is reserved for "never generated".
      const summary = meeting.summary ?? ''

      await MeetingsApi.update({ ...meeting, summary })

      meetingsById.value[meeting.id] = {
        ...meetingsById.value[meeting.id],
        title: meeting.title,
        completedAt: meeting.completedAt,
        summary,
      }

      htmlSummary.value[meeting.id] = await parseSummary({ summary })
    } catch (error) {
      SfLog.error('Failed to update meeting details:', { error })
    }
  }

  return {
    orderedMeetingIds,
    meetingsById,
    htmlSummary,
    setPastMeetings,
    setPastMeeting,
    fetchPastMeetings,
    fetchPastMeeting,
    setMeetingOrder,
    save,
    deleteMeeting,
  }
})
